import { isPlatformBrowser } from '@angular/common';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injector, PLATFORM_ID, inject } from '@angular/core';
import {
  Observable,
  catchError,
  finalize,
  from,
  map,
  of,
  switchMap,
  throwError,
} from 'rxjs';

import { ConnectivityService } from '../connectivity.service';
import { HttpCacheEntry, OutboxEntry } from '../models/offline-http.model';
import { newUuid } from '../repositories/base.repository';
import { SyncApiService } from '../sync/sync-api.service';
import { SyncService } from '../sync/sync.service';
import { HttpCacheRepository } from './http-cache.repository';
import {
  LOCAL_FIRST_MAX_AGE_MS,
  OFFLINE_BYPASS,
  OFFLINE_SOURCE_HEADER,
  cacheKeyFor,
  isNetworkFailure,
  localFirstClass,
  offlinePolicyFor,
  queuedWriteBody,
} from './offline-endpoints';
import { answersInWrite, isAffectedBy, withLocalAnswers, writeScope } from './offline-projections';
import { OutboxService } from './outbox.service';

/**
 * Where a teacher's screens get their data, and where their writes go.
 *
 * "Reachable" is ConnectivityService's confirmed answer - a real request to the
 * API's health endpoint - not navigator.onLine, which only knows whether a
 * network interface exists.
 *
 * Teacher-assessment reads (see localFirstClass) - the kept copy comes first:
 *   kept, under 15 min old   served from IndexedDB; no request at all
 *   kept, older              served from IndexedDB at once, refreshed in the
 *                            background for next time
 *   kept, touched by a save  fetched fresh while reachable (the kept copy if the
 *                            fetch fails); the kept copy while not
 *   not kept                 fetched, and kept
 * The first read of each screen after sign-in or a reload counts as "older", so
 * every session starts from current data without waiting for it.
 *
 * Other reads - the network comes first:
 *   reachable      the API answers, and its response is kept
 *   unreachable    the kept response; the network only when there is none
 *
 * Writes (the allow-list in offline-endpoints.ts):
 *   reachable      sent with an Idempotency-Key
 *   unreachable    stored in the outbox and answered 202 with `data.offline`
 *   fails midway   stored under the SAME key, so a write that did land and only
 *                  lost its response is recognised on replay, not applied twice
 * Either way, before the screen hears back, a saved answer is written into the
 * kept question screens and every kept screen the write touched is marked out
 * of date.
 *
 * Registered after httpErrorInterceptor, so a write stored offline reaches it as
 * a success and raises no network-error toast or redirect.
 */
export const offlineInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isPlatformBrowser(inject(PLATFORM_ID)) || req.context.get(OFFLINE_BYPASS)) {
    return next(req);
  }

  const policy = offlinePolicyFor(req);
  if (policy === 'network-only') return next(req);

  // Services are resolved lazily from the injector: several of them depend on
  // HttpClient, and resolving them eagerly here risks NG0200 (see
  // res.interceptor.ts for the same precaution).
  const injector = inject(Injector);
  return policy === 'cache-read'
    ? handleRead(req, next, injector)
    : handleWrite(req, next, injector);
};

// ── Reads ────────────────────────────────────────────────────────────────────

/** Keys with a background refresh in flight, so a busy screen does not stack them. */
const refreshing = new Set<string>();

/** When the current sign-in began; kept copies from before it are refreshed once. */
let sessionToken: string | null = null;
let sessionStartedAt = Date.now();

function currentSessionStart(session: SyncApiService): number {
  const token = session.bearerToken();
  if (token !== sessionToken) {
    sessionToken = token;
    sessionStartedAt = Date.now();
  }
  return sessionStartedAt;
}

function handleRead(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  injector: Injector,
): Observable<HttpEvent<unknown>> {
  const session = injector.get(SyncApiService);
  // No session, no owner: nothing is kept for nobody.
  const userKey = session.currentUserKey();
  if (!userKey) return next(req);

  const connectivity = injector.get(ConnectivityService);
  const cache = injector.get(HttpCacheRepository);
  const key = cacheKeyFor(userKey, req.urlWithParams, req.headers.get('school-id'));
  const kind = localFirstClass(req.urlWithParams);
  const keep = (body: unknown) =>
    cache.put({ key, userKey, url: req.urlWithParams, body, storedAt: Date.now() });

  // Network-first reads that can reach the API skip the local lookup entirely.
  if (!kind && connectivity.isOnline()) {
    return networkFirst(req, next, keep, () => cache.get(key), connectivity);
  }

  return from(cache.get(key)).pipe(
    switchMap((entry) => {
      const online = connectivity.isOnline();

      if (kind && entry) {
        if (!entry.invalidatedAt) {
          const fresh =
            Date.now() - entry.storedAt < LOCAL_FIRST_MAX_AGE_MS &&
            entry.storedAt >= currentSessionStart(session);
          if (!fresh && online) refreshInBackground(req, next, key, keep);
          return of(fromCache(req, entry));
        }
        // A save touched it: current data when it can be had, the kept copy
        // when it cannot.
        if (!online) return of(fromCache(req, entry));
        return networkFirst(req, next, keep, async () => entry, connectivity);
      }

      if (!online) return entry ? of(fromCache(req, entry)) : next(req);
      return networkFirst(req, next, keep, async () => entry, connectivity);
    }),
  );
}

/** The API, keeping what it returns; the kept copy only when there is no response. */
function networkFirst(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  keep: (body: unknown) => Promise<void>,
  fallback: () => Promise<HttpCacheEntry | undefined>,
  connectivity: ConnectivityService,
): Observable<HttpEvent<unknown>> {
  return next(req).pipe(
    switchMap((event) => {
      if (event instanceof HttpResponse && event.ok && event.body != null) {
        void keep(event.body);
      }
      return of(event);
    }),
    catchError((error: unknown) => {
      if (!isNetworkFailure(error)) return throwError(() => error);
      void connectivity.check();
      return from(fallback()).pipe(
        switchMap((entry) =>
          entry ? of(fromCache(req, entry)) : throwError(() => error),
        ),
      );
    }),
  );
}

/**
 * Fetch a newer copy for next time. The screen already has the kept one, so a
 * failure here changes nothing - the next read simply tries again.
 */
function refreshInBackground(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  key: string,
  keep: (body: unknown) => Promise<void>,
): void {
  if (refreshing.has(key)) return;
  refreshing.add(key);
  next(req)
    .pipe(finalize(() => refreshing.delete(key)))
    .subscribe({
      next: (event) => {
        if (event instanceof HttpResponse && event.ok && event.body != null) {
          void keep(event.body);
        }
      },
      error: () => undefined,
    });
}

// ── Writes ───────────────────────────────────────────────────────────────────

function handleWrite(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  injector: Injector,
): Observable<HttpEvent<unknown>> {
  const connectivity = injector.get(ConnectivityService);
  const idempotencyKey = req.headers.get('Idempotency-Key') ?? newUuid();
  const keyed = req.clone({ setHeaders: { 'Idempotency-Key': idempotencyKey } });

  /** Hand the response to the screen only once the kept screens reflect the write. */
  const afterLocalEffects = (response: HttpResponse<unknown>) =>
    from(applyToKeptResponses(injector, keyed, response.body)).pipe(map(() => response));

  const storeLocally = (cause: unknown): Observable<HttpEvent<unknown>> =>
    from(injector.get(OutboxService).enqueue(keyed, idempotencyKey)).pipe(
      map((entry) => queuedResponse(keyed, entry)),
      switchMap(afterLocalEffects),
      catchError((storeError: unknown) => {
        // Not stored anywhere. Report the failure the screen already knows how
        // to handle rather than pretending the work was saved.
        console.error('[offline] a write could not be stored on this device', storeError);
        return throwError(() =>
          cause instanceof HttpErrorResponse
            ? cause
            : new HttpErrorResponse({
                status: 0,
                statusText: 'Offline',
                url: keyed.urlWithParams,
                error: storeError,
              }),
        );
      }),
    );

  if (!connectivity.isOnline()) return storeLocally(null);

  return next(keyed).pipe(
    switchMap((event) => {
      if (!(event instanceof HttpResponse) || !event.ok) return of(event);
      // The server's derived rows - scores, progress - come down into the
      // entity tables.
      injector.get(SyncService).requestBackgroundSync();
      return afterLocalEffects(event);
    }),
    catchError((error: unknown) => {
      if (!isNetworkFailure(error)) return throwError(() => error);
      void connectivity.check();
      return storeLocally(error);
    }),
  );
}

/**
 * Write a saved answer into the kept question screens, then mark every kept
 * teacher screen the write touched as out of date. Never fails the write: the
 * work itself is already saved, on the server or in the outbox.
 */
async function applyToKeptResponses(
  injector: Injector,
  req: HttpRequest<unknown>,
  responseBody: unknown,
): Promise<void> {
  const userKey = injector.get(SyncApiService).currentUserKey();
  if (!userKey) return;
  const cache = injector.get(HttpCacheRepository);

  try {
    const answers = answersInWrite(req.urlWithParams, req.body);
    if (answers.length) {
      await cache.rewrite(userKey, (entry) => withLocalAnswers(entry.url, entry.body, answers));
    }

    const scope = writeScope(req.body, responseBody);
    await cache.invalidate(
      userKey,
      (entry) => localFirstClass(entry.url) === 'work' && isAffectedBy(entry.url, scope),
    );
  } catch (error) {
    console.warn('[offline] kept screens could not be updated after a save', error);
  }
}

function fromCache(req: HttpRequest<unknown>, entry: HttpCacheEntry): HttpResponse<unknown> {
  return new HttpResponse({
    status: 200,
    statusText: 'OK',
    url: req.urlWithParams,
    body: entry.body,
    headers: new HttpHeaders({
      [OFFLINE_SOURCE_HEADER]: 'cache',
      'x-sam-offline-stored-at': String(entry.storedAt),
    }),
  });
}

function queuedResponse(req: HttpRequest<unknown>, entry: OutboxEntry): HttpResponse<unknown> {
  return new HttpResponse({
    status: 202,
    statusText: 'Accepted',
    url: req.urlWithParams,
    body: queuedWriteBody(entry.idempotencyKey),
    headers: new HttpHeaders({ [OFFLINE_SOURCE_HEADER]: 'queued' }),
  });
}
